import { Component, Signal, WritableSignal, computed, inject, input, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { IonDatetime, IonItem, IonLabel, IonPopover } from '@ionic/angular/standalone';
import type { DatetimeChangeEventDetail, IonDatetimeCustomEvent } from '@ionic/core/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocaleDatePipe } from '@pipes/locale-date.pipe';
import { FORM_INPUT_VALIDATION_MESSAGES } from '@settings/form-validation-settings';
import { MbpFormErrorComponent } from '../mbp-form-error/mbp-form-error.component';

const DATETIME_LOCALES: Record<string, string> = {
  no: 'nb-NO',
  se: 'sv-SE',
  en: 'en-GB',
  da: 'da-DK',
  fi: 'fi-FI'
};

const DATE_PLACEHOLDERS: Record<string, string> = {
  no: 'DD.MM.YYYY',
  se: 'DD.MM.YYYY',
  en: 'DD/MM/YYYY',
  da: 'DD-MM-YYYY',
  fi: 'DD.MM.YYYY'
};

const MONTH_YEAR_PICKER_OPEN_CLASS = 'month-year-picker-open';

@Component({
  selector: 'mbp-calendar',
  templateUrl: './mbp-calendar.component.html',
  styleUrl: './mbp-calendar.component.scss',
  imports: [IonDatetime, IonItem, IonLabel, IonPopover, TranslateModule, LocaleDatePipe, MbpFormErrorComponent]
})
export class MbpCalendarComponent implements ControlValueAccessor {
  labelKey = input.required<string>();
  required = input<boolean>(false);
  errorMessages = input<Record<string, string> | undefined>(undefined);
  min = input<string | undefined>(undefined);
  max = input<string | undefined>(undefined);

  isRequired: Signal<boolean> = computed(() => {
    this.internalValue();
    return this.required() || (this.control()?.hasValidator(Validators.required) ?? false);
  });

  errorMessage: Signal<string | undefined> = computed(() => this.getError());

  internalValue: WritableSignal<string> = signal<string>('');

  protected readonly isOpen = signal(false);
  protected readonly popoverEvent = signal<Event | undefined>(undefined);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly translate = inject(TranslateService);
  private readonly ngControl: NgControl | undefined = inject(NgControl, { self: true, optional: true }) ?? undefined;

  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  private readonly currentLang: Signal<string> = computed(
    () => this.langChange()?.lang ?? this.translate.getCurrentLang() ?? this.translate.getFallbackLang() ?? 'en'
  );

  protected readonly datetimeLocale: Signal<string> = computed(
    () => DATETIME_LOCALES[this.currentLang()] ?? DATETIME_LOCALES['en']
  );
  protected readonly datePlaceholder: Signal<string> = computed(
    () => DATE_PLACEHOLDERS[this.currentLang()] ?? DATE_PLACEHOLDERS['en']
  );

  private readonly popover = viewChild.required(IonPopover);

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected presentPicker(event: Event): void {
    this.popoverEvent.set(event);
    this.isOpen.set(true);
  }

  protected async handleDateChange(event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>): Promise<void> {
    if (event.target.classList.contains(MONTH_YEAR_PICKER_OPEN_CLASS)) {
      return;
    }

    const nextValue = this.toDateOnly(event.detail.value);
    this.internalValue.set(nextValue);
    this.onChange(nextValue);
    this.isOpen.set(false);
    await this.popover().dismiss();
  }

  protected handleDismiss(): void {
    this.isOpen.set(false);
    this.onTouched();
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  private getError(): string | undefined {
    this.internalValue();

    const control = this.control();
    if (!control || !control.invalid || !(control.dirty || control.touched)) {
      return undefined;
    }

    const controlErrors: ValidationErrors | undefined = control.errors ?? undefined;
    if (!controlErrors) {
      return undefined;
    }

    for (const errorName of Object.keys(controlErrors)) {
      const mappedKey = this.errorMessages()?.[errorName] ?? FORM_INPUT_VALIDATION_MESSAGES[errorName];
      if (mappedKey) {
        return this.translate.instant(mappedKey);
      }
    }

    return this.translate.instant('form.validation.errors.invalid');
  }

  private control(): AbstractControl<unknown> | undefined {
    return (this.ngControl?.control as AbstractControl<unknown>) ?? undefined;
  }

  private toDateOnly(value: string | string[] | null | undefined): string {
    if (!value || Array.isArray(value)) {
      return '';
    }
    return value.slice(0, 10);
  }
}

